// src/pages/Dashboard.tsx — TrafficProcure multi-project dashboard
// Sprint 1-2: DashboardHeader + DashboardGrid (7 widgets) wired in at top.
// All existing panels (Procurement, FatigueManagement, ArchiveReadiness, etc.)
// preserved below the widget grid — will be converted to widgets in Sprint 5.
// B_PC users see only their assigned jobs — full edit on own, read-only on others.
// B_PM / A_DEVELOPER see all jobs with coordinator assignment badges.
import { useState, useEffect, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_DASHBOARD, PROJECT_TYPE_META, MOCK_POS, MOCK_JOBS } from '../utils/mockData'
import { getJobMaterialsSummary } from '../utils/workflowPipeline'
import { getLiveMilestones } from '../utils/mockDataClaims'
import { getAllHoldPointClearances } from '../utils/wiStepCompletion'
import {
  MOCK_FM_FLAGS, MOCK_ACCOMMODATION, MOCK_AWAY_REGISTER,
  EMPLOYEE_FM_PROFILES, FM_RISK_META, SITES, FM_THRESHOLDS,
} from '../utils/mockDataTravel'
import { getJobCoordinator } from '../utils/mockDataUsers'
import { MOCK_ALLOCATIONS, getJobWeekCrew, WEEK_DAYS } from '../utils/mockDataRoster'
import {
  MOCK_PROJECT_ARCHIVE_STATUS, MOCK_ARCHIVE_REQUESTS, getArchiveMetrics, archiveReadinessMeta,
} from '../utils/mockDataArchive'
import { formatCurrency, formatDate } from '../utils/formatters'
import { StatusBadge } from '../components/StatusBadge'
import { format } from 'date-fns'
import {
  Briefcase, AlertTriangle, Truck, RefreshCw, CheckCircle,
  Shield, ChevronRight, DollarSign, Activity, Users, Package,
  Bell, ShoppingCart, Layers, Bed, MapPin, Clock, Info, Calendar,
  Archive, Camera, FileText, ClipboardCheck, SlidersHorizontal,
} from 'lucide-react'

// ── Sprint 1: widget infrastructure ──────────────────────────────
import { DashboardHeader }   from '../components/Dashboard/DashboardHeader'
import { DashboardGrid }     from '../components/Dashboard/DashboardGrid'
import { WidgetTogglePanel } from '../components/Dashboard/WidgetTogglePanel'
import {
  WidgetId,
  getActiveWidgets,
  loadWidgetPrefs,
  saveWidgetPrefs,
} from '../components/Dashboard/types'
import { useScrollPosition } from '../utils/ScrollPositionContext'

// ── Sprint 2: 7 default widgets ───────────────────────────────────
import { ActiveJobsWidget }      from '../components/Dashboard/widgets/ActiveJobsWidget'
import { CrewOnSiteWidget }      from '../components/Dashboard/widgets/CrewOnSiteWidget'
import { SpendBudgetWidget }     from '../components/Dashboard/widgets/SpendBudgetWidget'
import { ProgressClaimsWidget }  from '../components/Dashboard/widgets/ProgressClaimsWidget'
import { SafetyAlertsWidget }    from '../components/Dashboard/widgets/SafetyAlertsWidget'
import { MilestonesWidget }      from '../components/Dashboard/widgets/MilestonesWidget'
import { ITPHoldPointsWidget }   from '../components/Dashboard/widgets/ITPHoldPointsWidget'
import { getArchiveRegister } from '../utils/archiveRegistry'

// ── Metric card ───────────────────────────────────────────────────
function MetricCard({
  icon: Icon, label, value, sub, iconBg = 'bg-blue-50', iconColor = 'text-blue-600',
  alert = false, suffix = '',
}: {
  icon: any; label: string; value: string | number
  sub?: string; iconBg?: string; iconColor?: string; alert?: boolean; suffix?: string
}) {
  const isAlert = alert && Number(value) > 0
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 transition-colors ${isAlert ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-2xl font-bold leading-none ${isAlert ? 'text-red-600' : 'text-gray-900'}`}>{value}{suffix}</div>
        <div className="text-sm text-gray-500 mt-1">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

// ── Utilisation gauge ─────────────────────────────────────────────
function UtilGauge({ value, label, warn = 85, danger = 95 }: {
  value: number; label: string; warn?: number; danger?: number
}) {
  const color    = value >= danger ? 'text-red-600'  : value >= warn ? 'text-amber-600' : 'text-green-600'
  const barColor = value >= danger ? 'bg-red-500'    : value >= warn ? 'bg-amber-500'   : 'bg-green-500'
  return (
    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-500">{label}</span>
        <span className={`text-xs font-bold ${color}`}>{value}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  )
}

// ── Coordinator badge (for PM view) ──────────────────────────────
function CoordBadge({ jobId }: { jobId: string }) {
  const coord = getJobCoordinator(jobId)
  if (!coord) return <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Unassigned</span>
  const initials = coord.fullName.split(' ').map((w: string) => w[0]).slice(0,2).join('')
  return (
    <span className="text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
      <span className="font-bold">{initials}</span>
      <span className="hidden sm:inline truncate max-w-[6rem]">{coord.fullName.split(' ')[0]}</span>
    </span>
  )
}

// ── Project row ───────────────────────────────────────────────────
function ProjectRow({
  project, showCoordinator, isOwned, isReadOnly, onNavigate,
}: {
  project: typeof MOCK_DASHBOARD.activeProjects[0]
  showCoordinator: boolean
  isOwned: boolean
  isReadOnly: boolean
  onNavigate?: (page: string) => void
}) {
  const meta = PROJECT_TYPE_META[project.type] ?? PROJECT_TYPE_META.SIGNALS
  const crewThisWeek = getJobWeekCrew(project.id, WEEK_DAYS, MOCK_ALLOCATIONS).length
  return (
    <div
      className={`flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer group ${isReadOnly ? 'opacity-60 hover:bg-gray-50/50' : 'hover:bg-gray-50'}`}
      onClick={() => onNavigate?.('jobs')}
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{project.jobNumber}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span>
          <StatusBadge status={project.status} />
          {project.openIssues > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
              {project.openIssues} issue{project.openIssues > 1 ? 's' : ''}
            </span>
          )}
          {showCoordinator && <CoordBadge jobId={project.id} />}
          {isReadOnly && (
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Read-only</span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">{project.description} · {project.client}</p>
      </div>
      <div className="hidden sm:flex flex-col items-end gap-1 w-24 flex-shrink-0">
        <span className="text-xs text-gray-500">{project.progressPercent}% done</span>
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-brand-blue rounded-full" style={{ width: `${project.progressPercent}%` }} />
        </div>
      </div>
      <div className="hidden md:flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{crewThisWeek > 0 ? crewThisWeek : project.crew} crew</span>
        <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{project.plantOnSite}</span>
      </div>
      <div className="hidden lg:block text-right flex-shrink-0 w-28">
        <div className="text-sm font-medium text-gray-900">{formatCurrency(project.contractValue)}</div>
        <div className="text-xs text-gray-400">committed {formatCurrency(project.poCommitted)}</div>
      </div>
      <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-colors ${isReadOnly ? 'text-gray-200' : 'text-gray-300 group-hover:text-gray-500'}`} />
    </div>
  )
}

// ── Commercial alert ──────────────────────────────────────────────
function CommercialAlert({ alert }: { alert: typeof MOCK_DASHBOARD.commercialAlerts[0] }) {
  const urgentStyle = alert.urgent ? 'border-red-200 bg-red-50'  : 'border-amber-100 bg-amber-50'
  const textStyle   = alert.urgent ? 'text-red-700'              : 'text-amber-800'
  const badgeStyle  = alert.urgent ? 'bg-red-100 text-red-700'   : 'bg-amber-100 text-amber-700'
  return (
    <div className={`mx-5 my-2 rounded-lg border p-3 flex items-start gap-3 ${urgentStyle}`}>
      <DollarSign className={`w-4 h-4 flex-shrink-0 mt-0.5 ${textStyle}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${badgeStyle}`}>{alert.jobNumber}</span>
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${badgeStyle}`}>
            {alert.type === 'VARIATION_NOTICE' ? 'Variation notice' : 'Progress claim'}
          </span>
        </div>
        <p className={`text-xs ${textStyle}`}>{alert.description}</p>
      </div>
      <div className={`text-xs font-bold flex-shrink-0 ${textStyle}`}>{alert.daysLeft}d left</div>
    </div>
  )
}

// ── Activity item ─────────────────────────────────────────────────
function ClipboardIcon(p: any) { return <Briefcase {...p} /> }
const ACTIVITY_ICONS: Record<string, any> = {
  SITE_DIARY: ClipboardIcon, PO_DELIVERY: Package, SWMS: Shield, VARIATION: DollarSign, NEAR_MISS: AlertTriangle,
}
const ACTIVITY_STYLES: Record<string, string> = {
  SITE_DIARY: 'bg-blue-100 text-blue-600', PO_DELIVERY: 'bg-green-100 text-green-600',
  SWMS: 'bg-purple-100 text-purple-600',    VARIATION: 'bg-amber-100 text-amber-600', NEAR_MISS: 'bg-red-100 text-red-600',
}
function ActivityItem({ item }: { item: typeof MOCK_DASHBOARD.recentActivity[0] }) {
  const Icon = ACTIVITY_ICONS[item.type] ?? Bell
  return (
    <div className="flex items-start gap-3 px-5 py-2.5 hover:bg-gray-50">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${ACTIVITY_STYLES[item.type] ?? 'bg-gray-100 text-gray-500'}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700">{item.text}</p>
        <p className="text-xs text-gray-400 mt-0.5">{item.user} · {item.time}</p>
      </div>
    </div>
  )
}

// ── Procurement by project ────────────────────────────────────────
function ProcurementByProject({
  onNavigateToPOs, scopedJobIds,
}: { onNavigateToPOs?: () => void; scopedJobIds: string[] | null }) {
  const [expanded, setExpanded] = useState(false)
  const allOpen = MOCK_POS.filter(p => !['DELIVERED','REJECTED'].includes(p.status))
  const baseJobs = scopedJobIds !== null ? MOCK_JOBS.filter(j => scopedJobIds.includes(j.id)) : MOCK_JOBS
  const jobData = baseJobs.map(job => {
    const allJobPOs       = MOCK_POS.filter(p => p.jobId === job.id)
    const openJobPOs      = allJobPOs.filter(p => !['DELIVERED','REJECTED'].includes(p.status))
    const pendingApproval = allJobPOs.filter(p => p.status === 'PENDING_APPROVAL')
    const inTransit       = allJobPOs.filter(p => ['APPROVED','SENT','PARTIALLY_DELIVERED'].includes(p.status))
    const openCommitted   = openJobPOs.reduce((s, p) => s + p.totalExGST, 0)
    const pct = job.contractValue > 0 ? Math.min(Math.round(openCommitted / job.contractValue * 100), 100) : 0
    return { job, allJobPOs, openJobPOs, pendingApproval, inTransit, openCommitted, pct }
  }).filter(d => d.allJobPOs.length > 0)
  const scopedOpenCommitted = jobData.reduce((s, d) => s + d.openCommitted, 0)
  const scopedPendingCount  = jobData.reduce((s, d) => s + d.pendingApproval.length, 0)
  const displayRows = expanded ? jobData : jobData.slice(0, 4)
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-brand-blue" />
          <h2 className="font-semibold text-gray-900">Procurement by project</h2>
          {scopedJobIds !== null && <span className="text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full">Your jobs</span>}
          {scopedPendingCount > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">⚠ {scopedPendingCount} pending approval</span>}
        </div>
        <button onClick={onNavigateToPOs} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          <Layers className="w-3 h-3" />View by job
        </button>
      </div>
      <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 border-b text-xs text-gray-400 font-medium">
        <div className="col-span-4">Job</div><div className="col-span-3">PO status</div>
        <div className="col-span-3">Commitment vs contract</div><div className="col-span-2 text-right">Open value</div>
      </div>
      <div className="divide-y divide-gray-100">
        {displayRows.map(({ job, allJobPOs, openJobPOs, pendingApproval, inTransit, openCommitted, pct }) => {
          const meta = PROJECT_TYPE_META[job.type] ?? PROJECT_TYPE_META.SIGNALS
          const deliveredCount = allJobPOs.filter(p => p.status === 'DELIVERED').length
          return (
            <div key={job.id} className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center transition-colors ${pendingApproval.length > 0 ? 'bg-amber-50/40' : 'hover:bg-gray-50/60'}`}>
              <div className="col-span-4 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                  <span className="text-sm font-semibold text-gray-900">{job.jobNumber}</span>
                  {pendingApproval.length > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold leading-none">⚠ {pendingApproval.length}</span>}
                </div>
                <p className="text-xs text-gray-400 truncate pl-3.5">{job.description.split('—')[0].trim()}</p>
              </div>
              <div className="col-span-3 flex flex-wrap gap-1">
                {pendingApproval.length > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium leading-tight">{pendingApproval.length} pending</span>}
                {inTransit.length > 0 && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium leading-tight">{inTransit.length} in transit</span>}
                {deliveredCount > 0 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium leading-tight">{deliveredCount} delivered</span>}
                {allJobPOs.length === 0 && <span className="text-xs text-gray-300">No POs</span>}
              </div>
              <div className="col-span-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${pct > 80 ? 'bg-amber-500' : 'bg-brand-blue'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{pct}%</span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5 tabular-nums">of {formatCurrency(job.contractValue)}</div>
              </div>
              <div className="col-span-2 text-right">
                {openCommitted > 0
                  ? <><div className="text-sm font-semibold text-gray-900 tabular-nums">{formatCurrency(openCommitted)}</div><div className="text-xs text-gray-400">{openJobPOs.length} open PO{openJobPOs.length !== 1 ? 's' : ''}</div></>
                  : <div className="text-xs text-green-600 font-medium flex items-center justify-end gap-1"><CheckCircle className="w-3 h-3" />All clear</div>
                }
              </div>
            </div>
          )
        })}
      </div>
      {jobData.length > 4 && (
        <button onClick={() => setExpanded(!expanded)} className="w-full px-5 py-2.5 border-t text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors text-center">
          {expanded ? '▲ Show less' : `▼ Show ${jobData.length - 4} more jobs`}
        </button>
      )}
      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-xs">
        <span className="text-gray-500">Open commitment{scopedJobIds !== null ? ' across your jobs' : ''}</span>
        <span className="font-bold text-gray-900 tabular-nums">{formatCurrency(scopedOpenCommitted)}</span>
      </div>
    </div>
  )
}

// ── Fatigue Management panel ──────────────────────────────────────
function FatigueManagementPanel({
  onNavigateToTravel, scopedJobIds,
}: { onNavigateToTravel?: () => void; scopedJobIds: string[] | null }) {
  const today = new Date('2026-06-17')
  const scopedFlags = scopedJobIds !== null ? MOCK_FM_FLAGS.filter(f => scopedJobIds.includes(f.jobId)) : MOCK_FM_FLAGS
  const redFlags    = scopedFlags.filter(f => f.riskLevel === 'RED')
  const amberFlags  = scopedFlags.filter(f => f.riskLevel === 'AMBER' && ['OPEN','PENDING_CONF'].includes(f.status))
  const yellowFlags = scopedFlags.filter(f => f.riskLevel === 'YELLOW')
  const openUrgent  = redFlags.length + amberFlags.length
  const approaching6Day = scopedFlags.filter(f => f.consecutiveDays >= FM_THRESHOLDS.CONSECUTIVE_MAX - 1 && f.consecutiveDays < FM_THRESHOLDS.CONSECUTIVE_MAX)
  const expiring48h = MOCK_ACCOMMODATION.filter(a => {
    if (a.status !== 'ACTIVE') return false
    if (scopedJobIds !== null && !scopedJobIds.includes(a.jobId)) return false
    const diff = Math.ceil((new Date(a.checkOut).getTime() - today.getTime()) / 86400000)
    return diff >= 0 && diff <= 2
  })
  const overdueReviews = Object.entries(EMPLOYEE_FM_PROFILES).filter(([, profile]) => {
    if (!profile.lastFMReviewDate) return true
    const monthsAgo = (today.getTime() - new Date(profile.lastFMReviewDate).getTime()) / (1000 * 60 * 60 * 24 * 30)
    return monthsAgo > 6
  })
  const EMP_NAMES: Record<string, string> = { e1:'Tony Rivera',e2:'Lisa Grant',e3:'Sam Nguyen',e4:'Ben Walsh',e5:'Karen Patel',e6:'Dave Fielding',e7:'Mike Torres',e8:'Sarah Kim' }
  const scopedAway = scopedJobIds !== null ? MOCK_AWAY_REGISTER.filter(e => scopedJobIds.includes(e.currentJobId)) : MOCK_AWAY_REGISTER
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className={`px-5 py-4 border-b flex items-center justify-between ${openUrgent > 0 ? 'bg-red-50' : 'bg-white'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className={`w-4 h-4 ${openUrgent > 0 ? 'text-red-600' : 'text-blue-600'}`} />
          <h2 className="font-semibold text-gray-900">Fatigue Management</h2>
          {scopedJobIds !== null && <span className="text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full">Your jobs</span>}
          {redFlags.length > 0   && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">{redFlags.length} RED</span>}
          {amberFlags.length > 0 && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">{amberFlags.length} AMBER</span>}
          {yellowFlags.length > 0 && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">{yellowFlags.length} YELLOW</span>}
          {openUrgent === 0 && redFlags.length === 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">All clear</span>}
        </div>
        <button onClick={onNavigateToTravel} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          <MapPin className="w-3 h-3" />Travel &amp; accom
        </button>
      </div>
      {openUrgent > 0 && (
        <div className="mx-5 mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 font-medium">{openUrgent} FM flag{openUrgent !== 1 ? 's' : ''} require PM action before affected roster entries can be published.</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x border-t mt-4">
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3"><MapPin className="w-3.5 h-3.5 text-purple-500" /><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Away from depot</span></div>
          {scopedAway.length === 0 ? <p className="text-xs text-gray-400">No employees currently away</p>
            : scopedAway.map(entry => {
                const rm  = FM_RISK_META[entry.riskLevel] ?? FM_RISK_META.GREEN
                const pct = Math.min(Math.round(entry.consecutiveDays / FM_THRESHOLDS.CONSECUTIVE_MAX * 100), 100)
                return (
                  <div key={entry.employeeId} className="mb-3">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-medium text-gray-900">{entry.employeeName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${rm.bg} ${rm.text}`}>{entry.riskLevel}</span>
                    </div>
                    <div className="text-xs text-gray-500">{entry.currentSite}</div>
                    <div className="text-xs text-gray-400">{entry.daysFromHome}d away · {entry.daysRemaining}d remaining</div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 tabular-nums">{entry.consecutiveDays}/{FM_THRESHOLDS.CONSECUTIVE_MAX}d</span>
                    </div>
                  </div>
                )
              })
          }
        </div>
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3"><Clock className="w-3.5 h-3.5 text-amber-500" /><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Near 6-day limit</span></div>
          {approaching6Day.length === 0 ? <p className="text-xs text-gray-400">No employees near 6-day limit</p>
            : approaching6Day.map(f => (
                <div key={f.id} className="mb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{f.employeeName}</span>
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">Day {f.consecutiveDays}</span>
                  </div>
                  <div className="text-xs text-gray-500">{f.jobNumber} · Rest day required after Day {FM_THRESHOLDS.CONSECUTIVE_MAX}</div>
                </div>
              ))
          }
        </div>
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3"><Bed className="w-3.5 h-3.5 text-blue-500" /><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Accommodation</span></div>
          {MOCK_ACCOMMODATION.filter(a => { if (!['ACTIVE','UPCOMING'].includes(a.status)) return false; if (scopedJobIds !== null && !scopedJobIds.includes(a.jobId)) return false; return true }).length === 0
            ? <p className="text-xs text-gray-400">No active accommodation records</p>
            : MOCK_ACCOMMODATION.filter(a => { if (!['ACTIVE','UPCOMING'].includes(a.status)) return false; if (scopedJobIds !== null && !scopedJobIds.includes(a.jobId)) return false; return true }).map(acc => {
                const isExpiring = expiring48h.includes(acc)
                return (
                  <div key={acc.id} className="mb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{acc.employeeName}</span>
                      {isExpiring ? <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">Check-out soon</span>
                        : <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${acc.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{acc.status.charAt(0)+acc.status.slice(1).toLowerCase()}</span>
                      }
                    </div>
                    <div className="text-xs text-gray-500 truncate">{acc.accommodationName}</div>
                    <div className="text-xs text-gray-400">{formatDate(acc.checkIn)} – {formatDate(acc.checkOut)}{!acc.employeeConfirmed && <span className="ml-1 text-amber-600">· unconfirmed</span>}</div>
                  </div>
                )
              })
          }
        </div>
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3"><Activity className="w-3.5 h-3.5 text-red-500" /><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reviews overdue</span></div>
          {overdueReviews.length === 0
            ? <p className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />All FM reviews current</p>
            : overdueReviews.map(([empId, profile]) => (
                <div key={empId} className="mb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{EMP_NAMES[empId] ?? empId}</span>
                    <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">Overdue</span>
                  </div>
                  <div className="text-xs text-gray-500">Last: {profile.lastFMReviewDate ? formatDate(profile.lastFMReviewDate) : 'Never reviewed'}</div>
                </div>
              ))
          }
          <p className="text-xs text-gray-400 mt-2">Annual review required — Safe Work Australia Code</p>
        </div>
      </div>
      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
        <span>WHS Act 2011 (QLD) · Safe Work Australia Code of Practice (Sep 2025)</span>
        <button onClick={onNavigateToTravel} className="text-blue-600 hover:underline flex items-center gap-1">Manage FM flags <ChevronRight className="w-3 h-3" /></button>
      </div>
    </div>
  )
}

// ── Roster summary ────────────────────────────────────────────────
function RosterSummaryCard({ scopedJobIds, onNavigate }: { scopedJobIds: string[] | null; onNavigate?: (page: string) => void }) {
  const allocs = MOCK_ALLOCATIONS.filter(a => WEEK_DAYS.includes(a.date))
  const scopedAllocs = scopedJobIds !== null ? allocs.filter(a => scopedJobIds.includes(a.jobId)) : allocs
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-teal-600" />
          <h3 className="font-semibold text-gray-900">This week's crew</h3>
          {scopedJobIds !== null && <span className="text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full">Your jobs</span>}
        </div>
        <button onClick={() => onNavigate?.('roster')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">Open roster <ChevronRight className="w-3 h-3" /></button>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-blue-50 rounded-lg p-3"><div className="text-2xl font-bold text-blue-700">{scopedAllocs.length}</div><div className="text-xs text-gray-500 mt-1">Employee-days scheduled</div></div>
        <div className="bg-green-50 rounded-lg p-3"><div className="text-2xl font-bold text-green-700">{new Set(scopedAllocs.map(a => a.employeeId)).size}</div><div className="text-xs text-gray-500 mt-1">Employees on roster</div></div>
        <div className="bg-amber-50 rounded-lg p-3"><div className="text-2xl font-bold text-amber-700">{scopedAllocs.filter(a => !a.timesheetId).length}</div><div className="text-xs text-gray-500 mt-1">Timesheet entries pending</div></div>
      </div>
    </div>
  )
}

type DashboardTemplate = 'EXECUTIVE' | 'PM' | 'PC' | 'COMMERCIAL' | 'WHS_QAQC'
const DASHBOARD_TEMPLATE_META: Record<DashboardTemplate, { label: string; focus: string; summary: string }> = {
  EXECUTIVE: { label: 'Managing Director', focus: 'Portfolio, risk, reporting, archive readiness', summary: 'Draft leadership view for portfolio status, reporting checkpoints, archive readiness, and commercial pressure points.' },
  PM:        { label: 'Project Manager',   focus: 'Delivery, field risk, variations, hold points', summary: 'Draft PM view for delivery control, field constraints, open variations, and jobs approaching close-out.' },
  PC:        { label: 'Project Controller',focus: 'Assigned jobs, records completeness, month-end actions', summary: 'Draft controller view for assigned projects, records completion, and archive or snapshot requests.' },
  COMMERCIAL:{ label: 'Commercial / Procurement', focus: 'Commitments, claims, reporting packs, package outputs', summary: 'Draft commercial view for procurement exposure, reporting pack generation, and package preparation.' },
  WHS_QAQC: { label: 'WHS / QAQC', focus: 'Compliance, hold points, inspections, QAQC evidence', summary: 'Draft WHS and QAQC view for evidence completeness, hold points, and report-ready records.' },
}
function recommendedTemplateForTier(tier?: string): DashboardTemplate {
  if (tier === 'B_PC') return 'PC'
  if (tier === 'C_PROCUREMENT') return 'COMMERCIAL'
  if (tier === 'D_FIELD') return 'WHS_QAQC'
  return 'PM'
}

function RoleTemplatePanel({ template, onChange, archiveMetrics, openFMFlags, scopedVariations, openHoldPoints, unreadWHSNotifications, onNavigate }: {
  template: DashboardTemplate; onChange: (t: DashboardTemplate) => void
  archiveMetrics: ReturnType<typeof getArchiveMetrics>; openFMFlags: number
  scopedVariations: number; openHoldPoints: number; unreadWHSNotifications: number
  onNavigate?: (page: string) => void
}) {
  const templateCards: Record<DashboardTemplate, { metrics: { label: string; value: string | number; icon: any; tone: string }[]; actions: { label: string; page: string }[] }> = {
    EXECUTIVE: { metrics: [{ label:'Jobs tracked',value:archiveMetrics.jobsTracked,icon:Briefcase,tone:'bg-blue-50 text-blue-700'},{label:'Snapshots due',value:archiveMetrics.snapshotsDueSoon,icon:Camera,tone:'bg-amber-50 text-amber-700'},{label:'Ready packages',value:archiveMetrics.readyPackages,icon:Archive,tone:'bg-green-50 text-green-700'},{label:'Open archive requests',value:archiveMetrics.openRequests,icon:FileText,tone:'bg-slate-100 text-slate-700'}], actions:[{label:'Project control',page:'jobs'},{label:'Reports',page:'reports'},{label:'Claims',page:'claims'}] },
    PM:        { metrics: [{ label:'FM flags open',value:openFMFlags,icon:Shield,tone:'bg-red-50 text-red-700'},{label:'Open variations',value:scopedVariations,icon:DollarSign,tone:'bg-amber-50 text-amber-700'},{label:'Hold points open',value:openHoldPoints,icon:ClipboardCheck,tone:'bg-orange-50 text-orange-700'},{label:'Archive-ready jobs',value:archiveMetrics.nearArchiveReady,icon:Archive,tone:'bg-green-50 text-green-700'}], actions:[{label:'Travel',page:'travel'},{label:'Claims',page:'claims'},{label:'Reports',page:'reports'}] },
    PC:        { metrics: [{ label:'Assigned jobs',value:archiveMetrics.jobsTracked,icon:Briefcase,tone:'bg-blue-50 text-blue-700'},{label:'Snapshots due',value:archiveMetrics.snapshotsDueSoon,icon:Calendar,tone:'bg-amber-50 text-amber-700'},{label:'Archive requests open',value:archiveMetrics.openRequests,icon:FileText,tone:'bg-slate-100 text-slate-700'},{label:'QAQC at risk',value:archiveMetrics.qaqcAtRisk,icon:AlertTriangle,tone:'bg-red-50 text-red-700'}], actions:[{label:'Project control',page:'jobs'},{label:'Site diary',page:'site-diary'},{label:'ITP',page:'itp'}] },
    COMMERCIAL:{ metrics: [{ label:'Archive requests open',value:archiveMetrics.openRequests,icon:Archive,tone:'bg-slate-100 text-slate-700'},{label:'Snapshots due',value:archiveMetrics.snapshotsDueSoon,icon:Camera,tone:'bg-amber-50 text-amber-700'},{label:'Ready report packs',value:archiveMetrics.readyPackages,icon:FileText,tone:'bg-green-50 text-green-700'},{label:'Open variations',value:scopedVariations,icon:DollarSign,tone:'bg-blue-50 text-blue-700'}], actions:[{label:'Purchase orders',page:'pos'},{label:'RFQ portal',page:'rfq'},{label:'Claims',page:'claims'}] },
    WHS_QAQC: { metrics: [{ label:'SWMS pending',value:unreadWHSNotifications,icon:Shield,tone:'bg-red-50 text-red-700'},{label:'Hold points open',value:openHoldPoints,icon:ClipboardCheck,tone:'bg-amber-50 text-amber-700'},{label:'QAQC at risk',value:archiveMetrics.qaqcAtRisk,icon:AlertTriangle,tone:'bg-orange-50 text-orange-700'},{label:'Snapshots due',value:archiveMetrics.snapshotsDueSoon,icon:Camera,tone:'bg-blue-50 text-blue-700'}], actions:[{label:'Permits',page:'permits'},{label:'Inspections',page:'inspections'},{label:'Environmental',page:'environmental'}] },
  }
  const config = templateCards[template]
  const meta   = DASHBOARD_TEMPLATE_META[template]
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-slate-50">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap"><h2 className="font-semibold text-gray-900">Draft role dashboard template</h2><span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium">{meta.label}</span></div>
            <p className="text-sm text-gray-600 mt-1">{meta.summary}</p>
            <p className="text-xs text-gray-400 mt-1">Focus: {meta.focus}</p>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {(Object.keys(DASHBOARD_TEMPLATE_META) as DashboardTemplate[]).map(key => (
              <button key={key} onClick={() => onChange(key)} className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${template === key ? 'bg-brand-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{DASHBOARD_TEMPLATE_META[key].label}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-5 py-4">
        {config.metrics.map(metric => { const Icon = metric.icon; return (
          <div key={metric.label} className="border border-gray-200 rounded-xl p-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${metric.tone}`}><Icon className="w-5 h-5" /></div>
            <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
            <div className="text-sm text-gray-500 mt-1">{metric.label}</div>
          </div>
        )})}
      </div>
      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-gray-500">Template actions are draft workflow shortcuts for the next beta round.</span>
        <div className="flex items-center gap-2 flex-wrap">
          {config.actions.map(action => (
            <button key={action.label} onClick={() => onNavigate?.(action.page)} className="text-xs bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg">{action.label}</button>
          ))}
        </div>
      </div>
    </div>
  )
}

function ArchiveReadinessPanel({ scopedJobIds, onNavigate }: { scopedJobIds: string[] | null; onNavigate?: (page: string) => void }) {
  const metrics = getArchiveMetrics(scopedJobIds)
  const archiveStatuses = scopedJobIds === null ? MOCK_PROJECT_ARCHIVE_STATUS : MOCK_PROJECT_ARCHIVE_STATUS.filter(s => scopedJobIds.includes(s.jobId))
  const topJobs = [...archiveStatuses].sort((a, b) => b.archiveReadyPct - a.archiveReadyPct).slice(0, 4)
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Archive className="w-4 h-4 text-brand-blue" /><h2 className="font-semibold text-gray-900">Archive &amp; snapshot readiness</h2></div>
          <p className="text-sm text-gray-500 mt-1">Draft archive controls for project close-out, month-end, and year-end reporting outputs.</p>
        </div>
        <button onClick={() => onNavigate?.('jobs')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">Open project control <ChevronRight className="w-3 h-3" /></button>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 px-5 py-4 border-b">
        {[
          { label:'Snapshots due now',value:metrics.snapshotsDueSoon,tone:'bg-amber-50 text-amber-700',icon:Camera },
          { label:'Open archive requests',value:metrics.openRequests,tone:'bg-slate-100 text-slate-700',icon:FileText },
          { label:'Ready packages',value:metrics.readyPackages,tone:'bg-green-50 text-green-700',icon:Archive },
          { label:'QAQC at risk',value:metrics.qaqcAtRisk,tone:'bg-red-50 text-red-700',icon:ClipboardCheck },
        ].map(item => { const Icon = item.icon; return (
          <div key={item.label} className="rounded-xl border border-gray-200 p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${item.tone}`}><Icon className="w-4 h-4" /></div>
            <div className="text-xl font-bold text-gray-900">{item.value}</div>
            <div className="text-xs text-gray-500 mt-1">{item.label}</div>
          </div>
        )})}
      </div>
      <div className="divide-y divide-gray-100">
        {topJobs.map(status => {
          const job = MOCK_JOBS.find(item => item.id === status.jobId)
          const readiness = archiveReadinessMeta(status.archiveReadyPct)
          return (
            <div key={status.jobId} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${readiness.bg} ${readiness.text}`}><Archive className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{job?.jobNumber ?? status.jobId}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${readiness.bg} ${readiness.text}`}>{readiness.label}</span>
                  {status.nextSnapshotDueDate && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Snapshot due {formatDate(status.nextSnapshotDueDate)}</span>}
                </div>
                <p className="text-sm text-gray-600 mt-0.5 truncate">{job?.description ?? 'Project archive status'}</p>
                <p className="text-xs text-gray-400 mt-1">Ready {status.archiveReadyPct}% · {status.blockingItems.length} blocker{status.blockingItems.length !== 1 ? 's' : ''} · {status.recommendedAction}</p>
              </div>
              <div className="hidden lg:block text-right flex-shrink-0">
                <div className="text-sm font-semibold text-gray-900">{status.archiveReadyPct}%</div>
                <div className="text-xs text-gray-400">overall readiness</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────────
export function Dashboard({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { user, isTier, scopedJobIds } = useAuthStore()
  const isAdmin = isTier('B_PM')
  const isPC    = user?.tier === 'B_PC'
  const jIds    = scopedJobIds()

  // ── Scroll restore on mount ────────────────────────────────────
  const { restorePosition } = useScrollPosition()
  useEffect(() => { restorePosition('dashboard') }, [])

  // ── Widget preferences ─────────────────────────────────────────
  const userId = user?.id ?? 'default'
  const [showPanel,   setShowPanel]   = useState(false)
  const [widgetPrefs, setWidgetPrefs] = useState<Partial<Record<WidgetId, boolean>>>(
    () => loadWidgetPrefs(userId),
  )

  const handleToggle = (id: WidgetId, visible: boolean) => {
    const next = { ...widgetPrefs, [id]: visible }
    setWidgetPrefs(next)
    saveWidgetPrefs(userId, next)
  }

  const userTier     = user?.tier ?? 'D_FIELD'
  const activeWidgets = getActiveWidgets(userTier, widgetPrefs)

  // ── Render widget by id ────────────────────────────────────────
  const nav = (page: string) => onNavigate?.(page)
  const widgetProps = { onNavigate: nav, scopedJobIds: jIds }

  const renderWidget = (id: string): ReactNode => {
    switch (id) {
      case 'active-jobs':      return <ActiveJobsWidget      {...widgetProps} />
      case 'crew-onsite':      return <CrewOnSiteWidget      {...widgetProps} />
      case 'spend-budget':     return <SpendBudgetWidget     {...widgetProps} />
      case 'progress-claims':  return <ProgressClaimsWidget  {...widgetProps} />
      case 'safety-alerts':    return <SafetyAlertsWidget    {...widgetProps} />
      case 'milestones':       return <MilestonesWidget      {...widgetProps} />
      case 'itp-hold-points':  return <ITPHoldPointsWidget   {...widgetProps} />
      // Sprint 5 widgets — stub null until built
      default: return null
    }
  }

  // ── Existing dashboard data ────────────────────────────────────
  const [activeFilter, setActiveFilter]           = useState<string>('ALL')
  const [dashboardTemplate, setDashboardTemplate] = useState<DashboardTemplate>(recommendedTemplateForTier(user?.tier))

  const { data, refetch, isError } = useQuery({
    queryKey: ['dashboard'],
    queryFn:  () => api.get('/api/dashboard/summary').then(r => r.data.data),
    retry: 1, staleTime: 60_000,
  })

  const d        = MOCK_DASHBOARD
  const liveData = !isError && data ? (data as any) : null
  const liveKpis = liveData?.kpis ?? null
  const liveComplianceAlerts: any[] | null = liveData?.complianceAlerts ?? null
  const usingMock = isError || !data

  const allProjects   = d.activeProjects as typeof MOCK_DASHBOARD.activeProjects
  const myProjects    = jIds !== null ? allProjects.filter((p: any) => jIds.includes(p.id)) : allProjects
  const otherProjects = jIds !== null ? allProjects.filter((p: any) => !jIds.includes(p.id)) : []
  const filteredMy    = activeFilter === 'ALL' ? myProjects  : myProjects.filter( (p: any) => p.type === activeFilter)
  const filteredOther = activeFilter === 'ALL' ? otherProjects : otherProjects.filter((p: any) => p.type === activeFilter)

  const scopedVariations = jIds !== null
    ? (d.commercialAlerts?.filter((a: any) => jIds.includes(a.jobId))?.length ?? 0)
    : d.metrics.openVariations
  const openFMFlags    = MOCK_FM_FLAGS.filter(f => {
    if (!['OPEN','PENDING_CONF'].includes(f.status)) return false
    if (jIds !== null && !jIds.includes(f.jobId)) return false
    return true
  }).length
  const archiveMetrics = getArchiveMetrics(jIds)

  return (
    <div className="space-y-6">

      {/* ── 1. Hardwired header (time / date / weather) ─────────── */}
      <DashboardHeader />

      {/* ── 2. Widget grid ──────────────────────────────────────── */}
      <div>
        {/* Customise button */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900">Overview</h2>
            <p className="text-xs text-gray-400 mt-0.5">{activeWidgets.length} widget{activeWidgets.length !== 1 ? 's' : ''} active</p>
          </div>
          <button
            onClick={() => setShowPanel(true)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Customise dashboard
          </button>
        </div>

        {/* Grid */}
        <DashboardGrid activeWidgets={activeWidgets} renderWidget={renderWidget} />
      </div>

      {/* Widget toggle panel */}
      {showPanel && (
        <WidgetTogglePanel
          userTier={userTier}
          prefs={widgetPrefs}
          onChange={handleToggle}
          onClose={() => setShowPanel(false)}
        />
      )}

      {/* ── 3-onwards: Legacy panels (preserved from previous build) ── */}

      {/* Greeting & refresh */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'},{' '}
            {user?.fullName?.split(' ')[0]}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {format(new Date(), 'EEEE, d MMMM yyyy')}
            {!usingMock && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">● Live data</span>}
            {usingMock  && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Prototype — mock data</span>}
          </p>
        </div>
        <button onClick={() => refetch()} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
          <RefreshCw className="w-4 h-4" />Refresh
        </button>
      </div>

      {/* PC scope banner */}
      {isPC && jIds !== null && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl px-5 py-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-sky-800">
            <strong>Project Coordinator view</strong> — full edit on{' '}
            <strong>{jIds.length} assigned job{jIds.length !== 1 ? 's' : ''}</strong>
            {' '}({jIds.join(', ')}). Other jobs shown as read-only.
          </div>
        </div>
      )}

      <RoleTemplatePanel
        template={dashboardTemplate}
        onChange={setDashboardTemplate}
        archiveMetrics={archiveMetrics}
        openFMFlags={openFMFlags}
        scopedVariations={scopedVariations}
        openHoldPoints={d.metrics.openHoldPoints}
        unreadWHSNotifications={d.metrics.unreadWHSNotifications}
        onNavigate={onNavigate}
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        <MetricCard icon={Briefcase}     label="Active jobs"       value={isPC ? myProjects.length : (liveKpis?.activeJobs ?? d.metrics.activeJobs)}            iconBg="bg-blue-50"   iconColor="text-blue-600" />
        <MetricCard icon={DollarSign}    label="Open variations"   value={scopedVariations}                              alert         iconBg="bg-amber-50"  iconColor="text-amber-600" />
        <MetricCard icon={Shield}        label="FM flags open"     value={openFMFlags}                                   alert         iconBg="bg-orange-50" iconColor="text-orange-600" />
        <MetricCard icon={AlertTriangle} label="Open incidents"    value={liveKpis?.openIncidents ?? d.metrics.unreadWHSNotifications} alert iconBg="bg-red-50" iconColor="text-red-600" />
        <MetricCard icon={Activity}      label="Plant utilisation" value={d.metrics.plantUtilisation} suffix="%"
          iconBg={d.metrics.plantUtilisation > 90 ? 'bg-red-50' : 'bg-green-50'}
          iconColor={d.metrics.plantUtilisation > 90 ? 'text-red-600' : 'text-green-600'} />
        <MetricCard icon={Users}         label="Crew utilisation"  value={d.metrics.crewUtilisation} suffix="%"
          iconBg={d.metrics.crewUtilisation > 95 ? 'bg-red-50' : 'bg-teal-50'}
          iconColor={d.metrics.crewUtilisation > 95 ? 'text-red-600' : 'text-teal-600'} />
      </div>

      {/* Backbone pipeline */}
      {(() => {
        const activeJobs = (jIds !== null ? MOCK_JOBS.filter((j: any) => jIds.includes(j.id)) : MOCK_JOBS).filter((j: any) => j.status === 'ACTIVE')
        const jobSummaries = activeJobs.map((j: any) => getJobMaterialsSummary(j.id, j.jobNumber))
        const eligibleMilestones = getLiveMilestones().filter(m => m.status === 'ELIGIBLE' && (jIds === null || jIds.includes(m.jobId)))
        const holdPointsCleared  = getAllHoldPointClearances().length
        const complianceIssues   = jobSummaries.reduce((s, j) => s + j.complianceIssues, 0)
        const readyJobs          = jobSummaries.filter(j => j.readyForWork).length
        const pooledTransfers    = jobSummaries.reduce((s, j) => s + j.pooledLines, 0)
        return (
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5"><ClipboardCheck className="w-4 h-4 text-indigo-600" /> Backbone pipeline status</h2>
              <span className="text-xs text-gray-400">Live — updated from field records</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { label:'Jobs materials-ready',value:readyJobs,total:activeJobs.length,ok:readyJobs===activeJobs.length,warn:readyJobs<activeJobs.length,sub:`${activeJobs.length} active jobs` },
                { label:'Milestones eligible to claim',value:eligibleMilestones.length,total:null,ok:eligibleMilestones.length>0,warn:false,sub:eligibleMilestones.length>0?'action recommended':'none eligible yet' },
                { label:'Compliance issues',value:complianceIssues,total:null,ok:complianceIssues===0,warn:complianceIssues>0,sub:complianceIssues>0?'BOM lines need review':'all clear' },
                { label:'Hold points cleared',value:holdPointsCleared,total:null,ok:holdPointsCleared>0,warn:false,sub:'across all active jobs' },
                { label:'Material pool transfers',value:pooledTransfers,total:null,ok:true,warn:false,sub:'cross-job reuse' },
              ].map(c => (
                <div key={c.label} className={`rounded-xl border p-3 ${c.warn?'bg-amber-50 border-amber-200':c.ok&&c.value>0?'bg-green-50 border-green-200':'bg-gray-50 border-gray-200'}`}>
                  <div className="text-xs text-gray-500 leading-tight mb-1">{c.label}</div>
                  <div className={`text-2xl font-bold ${c.warn?'text-amber-700':c.ok&&c.value>0?'text-green-700':'text-gray-900'}`}>{c.value}{c.total!==null?<span className="text-base font-normal text-gray-400">/{c.total}</span>:null}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{c.sub}</div>
                </div>
              ))}
            </div>
            {complianceIssues > 0 && (
              <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                <span><strong>{complianceIssues} BOM line{complianceIssues!==1?'s':''}</strong> with compliance issues. Check Jobs → BOM tab.</span>
              </div>
            )}
          </div>
        )
      })()}

      {/* Admin quick stats */}
      {isAdmin && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label:'Open PO commitment',    value:formatCurrency(d.poValueSummary?.totalValueAud??0), sub:`${d.poValueSummary?.count??0} orders in transit`,warn:false },
            { label:'SWMS pending approval', value:d.hseStatus.swmsAwaitingApproval,sub:'need sign-off before site',warn:d.hseStatus.swmsAwaitingApproval>0 },
            { label:'ITP hold points open',  value:d.metrics.openHoldPoints,sub:'awaiting release',warn:d.metrics.openHoldPoints>0 },
            { label:'Employees away',        value:MOCK_AWAY_REGISTER.length,sub:MOCK_AWAY_REGISTER.length>0?'from home depot — FM active':'all at home depot',warn:MOCK_AWAY_REGISTER.length>0 },
          ].map(c => (
            <div key={c.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs text-gray-500 mb-1">{c.label}</div>
              <div className={`text-xl font-bold ${c.warn?'text-amber-600':'text-gray-900'}`}>{c.value}</div>
              <div className="text-xs text-gray-400">{c.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Resource utilisation */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Resource utilisation</h2>
          <span className="text-xs text-gray-400">{d.metrics.activeJobs} active projects</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UtilGauge value={d.metrics.crewUtilisation}  label="Crew capacity" />
          <UtilGauge value={d.metrics.plantUtilisation} label="Plant &amp; fleet" />
        </div>
        {d.metrics.crewUtilisation >= 90 && (
          <p className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Crew at {d.metrics.crewUtilisation}% — consider subcontractor augmentation for incoming work
          </p>
        )}
      </div>

      {/* Roster summary */}
      <RosterSummaryCard scopedJobIds={jIds} onNavigate={onNavigate} />

      {/* Active projects */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-900">{isPC ? `Your projects (${myProjects.length})` : 'Active projects'}</h2>
            {isAdmin && (
              <button onClick={() => onNavigate?.('settings')} className="text-xs bg-sky-100 text-sky-700 hover:bg-sky-200 px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors">
                Assign to coordinators
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {['ALL','SIGNALS','CIVIL','ENERGY_GRID'].map(f => (
              <button key={f} onClick={() => setActiveFilter(f)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${activeFilter===f?'bg-brand-navy text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {f==='ALL'?'All':(PROJECT_TYPE_META as any)[f]?.label??f}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredMy.length===0
            ? <div className="px-5 py-10 text-center text-sm text-gray-400"><CheckCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />No projects matching filter</div>
            : filteredMy.map((p: any) => <ProjectRow key={p.id} project={p} showCoordinator={isAdmin&&!isPC} isOwned={true} isReadOnly={false} onNavigate={onNavigate} />)
          }
        </div>
        {isPC && filteredOther.length > 0 && (
          <>
            <div className="px-5 py-2.5 border-t bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">Other coordinators' jobs — read-only</div>
            <div className="divide-y divide-gray-100">
              {filteredOther.map((p: any) => <ProjectRow key={p.id} project={p} showCoordinator={true} isOwned={false} isReadOnly={true} onNavigate={onNavigate} />)}
            </div>
          </>
        )}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>{isPC?`${myProjects.length} assigned · ${otherProjects.length} other`:`${allProjects.length} projects`} · total value {formatCurrency(myProjects.reduce((s: number, p: any) => s + p.contractValue, 0))}</span>
          {isAdmin && <button className="text-blue-600 hover:underline flex items-center gap-1">All jobs <ChevronRight className="w-3 h-3" /></button>}
        </div>
      </div>

      <ProcurementByProject onNavigateToPOs={onNavigate ? () => onNavigate('pos') : undefined} scopedJobIds={jIds} />
      <FatigueManagementPanel onNavigateToTravel={onNavigate ? () => onNavigate('travel') : undefined} scopedJobIds={jIds} />
      <ArchiveReadinessPanel scopedJobIds={jIds} onNavigate={onNavigate} />

      {/* Lower 3-col */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {(isAdmin||isPC) && d.commercialAlerts?.filter((a: any) => jIds===null||jIds.includes(a.jobId)).length>0 && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-600" /><h3 className="font-semibold text-gray-900">Commercial alerts</h3></div>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{d.commercialAlerts.filter((a: any) => jIds===null||jIds.includes(a.jobId)).length} pending</span>
            </div>
            <div className="py-2">{d.commercialAlerts.filter((a: any) => jIds===null||jIds.includes(a.jobId)).map((a: any) => <CommercialAlert key={a.id} alert={a} />)}</div>
          </div>
        )}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-blue-600" /><h3 className="font-semibold text-gray-900">Plant &amp; fleet</h3></div>
            {d.resourceStatus.maintenanceDue>0&&<span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{d.resourceStatus.maintenanceDue} service due</span>}
          </div>
          <div className="divide-y divide-gray-100">
            {d.resourceStatus.plant.map((item: any) => (
              <div key={item.id} className="px-5 py-3 flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status==='ALLOCATED'?'bg-blue-500':item.status==='AVAILABLE'?'bg-green-500':'bg-orange-400'}`} />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>{item.jobNumber&&<div className="text-xs text-gray-400">{item.jobNumber}</div>}</div>
                <StatusBadge status={item.status} />
              </div>
            ))}
          </div>
          <div className={`px-5 py-3 border-t flex items-center gap-2 text-xs ${d.resourceStatus.conflictsDetected===0?'bg-green-50 text-green-700':'bg-red-50 text-red-700'}`}>
            <CheckCircle className="w-3.5 h-3.5" />{d.resourceStatus.conflictsDetected===0?'No allocation conflicts':`${d.resourceStatus.conflictsDetected} conflict(s) — review resource matrix`}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Recent activity</h3></div>
          <div className="divide-y divide-gray-50 py-1">{d.recentActivity.map((item: any) => <ActivityItem key={item.id} item={item} />)}</div>
          <div className="px-5 py-3 border-t"><button className="text-xs text-blue-600 hover:underline flex items-center gap-1">View audit log <ChevronRight className="w-3 h-3" /></button></div>
        </div>
      </div>

      {((liveComplianceAlerts ?? d.vehicleComplianceAlerts)?.length > 0) && (() => {
        const alerts = liveComplianceAlerts ?? d.vehicleComplianceAlerts
        return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-orange-600" /><h2 className="font-semibold text-gray-900">Vehicle &amp; equipment compliance</h2></div>
            <div className="flex items-center gap-2">
              {liveComplianceAlerts && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">● Live</span>}
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{alerts.length} alert{alerts.length!==1?'s':''}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {alerts.map((alert: any, i: number) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0"><Truck className="w-4 h-4 text-orange-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{alert.make} {alert.model}{alert.plate!=='N/A'?` — ${alert.plate}`:''}</div>
                  <div className="flex items-center gap-2 mt-0.5"><StatusBadge status={alert.complianceType} /><span className="text-xs text-gray-400">due {formatDate(alert.dueDate)}</span></div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </div>
            ))}
          </div>
        </div>
        )
      })()}

      <p className="text-xs text-gray-400 text-center pb-2">
        Auto-refreshes every 60s · <button onClick={() => refetch()} className="underline hover:text-gray-600">Refresh now</button>
        {usingMock ? ' · Mock data active' : ' · Live data · API connected'}
      </p>
    </div>
  )
}
